// The calendar always opens directly below its field (see `drops: 'down'`
// below), so two things can push it out of view: a field near the top of
// the page puts the calendar right up against (or behind) the fixed navbar,
// and a field near the bottom of a long form/page lets the calendar's own
// height run past the bottom of the viewport, hiding everything below
// wherever the visible area cuts off. Both are corrected the same way — a
// single position adjustment made right when it opens — since they're
// mutually exclusive (a field can't be simultaneously too close to the top
// and too close to the bottom).
//
// The container is kept hidden (visibility, not display — daterangepicker
// still needs it laid out to measure) until this correction has been
// applied, and only revealed on the next animation frame, so there's never
// a frame where the wrong position is visible.
function clampCalendarBelowNavbar(picker) {
  if (!picker || !picker.container || !picker.container.length) return;

  const containerEl = picker.container[0];
  containerEl.style.visibility = "hidden";

  requestAnimationFrame(function () {
    const navbar = document.querySelector(".header-navbar.fixed-top") || document.querySelector(".fixed-top");
    const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
    const buffer = 8;
    const safeTop = navbarHeight + buffer;
    const safeBottom = window.innerHeight - buffer;

    const rect = containerEl.getBoundingClientRect();
    const naturalTop = parseFloat(picker.container.css("top")) || 0;
    let delta = 0;

    if (rect.top < safeTop) {
      delta = safeTop - rect.top;
    } else if (rect.bottom > safeBottom) {
      delta = -(rect.bottom - safeBottom);
      // Never push it back up past the navbar to fit it — if the calendar
      // is simply taller than the space available, keep its top (and the
      // "today"/controls it starts with) visible over its tail end.
      if (rect.top + delta < safeTop) delta = safeTop - rect.top;
    }

    if (delta !== 0) {
      picker.container.css("top", (naturalTop + delta) + "px");
    }

    containerEl.style.visibility = "";
  });
}

// If the page keeps scrolling while the calendar is open, close it — the
// same thing the app's other dropdowns (Select2) already do — rather than
// trying to keep it open and follow the field down the page. That tracking
// behavior technically worked, but it made the calendar visibly detach from
// its field and hover on its own, which reads as broken even though it
// isn't. Closing on scroll keeps every dropdown in the app behaving the
// same way: scroll away, and it closes.
function watchCalendarForNavbarOverlap($field) {
  $field.off("show.daterangepicker.navbarClear hide.daterangepicker.navbarClear");
  $(window).off("scroll.navbarClear");

  $field.on("show.daterangepicker.navbarClear", function () {
    const picker = $field.data("daterangepicker");
    if (!picker) return;

    clampCalendarBelowNavbar(picker);

    $(window).on("scroll.navbarClear", function () {
      picker.hide();
    });
  });

  $field.on("hide.daterangepicker.navbarClear", function () {
    $(window).off("scroll.navbarClear");
  });
}

// Single-date picker (daterangepicker in singleDatePicker mode) used anywhere a
// form previously used a native <input type="date">. The visible field keeps the
// "MM/DD/YYYY" display format used throughout the app; a companion hidden field
// carries the value back to the server as "YYYY-MM-DD" so nothing server-side
// (sorting, ransack, string-column reads) has to change.
//
// Markup expected:
//   <input type="text" id="{displayId}" class="form-control">
//   <input type="hidden" id="{hiddenId}" name="..." value="<%= existing_value %>">
export function setupSingleDatePicker(displayId, hiddenId, options = {}) {
  const $display = $("#" + displayId);
  const $hidden = $("#" + hiddenId);
  if (!$display.length || !$hidden.length) return;

  if ($display.data("daterangepicker")) {
    $display.data("daterangepicker").remove();
    $display.removeData("daterangepicker");
  }
  $display.off("apply.daterangepicker blur.singleDatePicker change.singleDatePicker");

  const existing = moment($hidden.val(), "YYYY-MM-DD", true);
  const isNarrow = window.matchMedia("(max-width: 563.98px)").matches;
  const autoApply = options.autoApply === true;
  const readOnly = options.readOnly === true;
  const minDate = options.minDate
    ? moment(options.minDate, "YYYY-MM-DD", true).startOf("day")
    : null;
  const minDateValid = minDate && minDate.isValid();

  let startDate = existing.isValid() ? existing.clone() : moment();
  if (minDateValid && startDate.isBefore(minDate, "day")) {
    startDate = minDate.clone();
  }

  const pickerOptions = {
    singleDatePicker: true,
    autoApply: autoApply,
    autoUpdateInput: false,
    opens: isNarrow ? "center" : (options.opens || "left"),
    drops: "down",
    locale: { format: "MM/DD/YYYY" },
    startDate: startDate
  };

  // Only when caller passes minDate (e.g. checkout). Other pages omit it and stay unchanged.
  if (minDateValid) {
    pickerOptions.minDate = minDate;
  }

  // Only mutate the input when this caller opts in — leave other pages alone.
  if (readOnly) {
    $display.prop("readonly", true);
    $display.attr("autocomplete", "off");
    $display.css("caret-color", "transparent");
  }

  $display.daterangepicker(pickerOptions);
  watchCalendarForNavbarOverlap($display);
  $display.val(existing.isValid() ? existing.format("MM/DD/YYYY") : "");

  function syncFromInput() {
    if (readOnly) return;

    const parsed = moment($display.val(), "MM/DD/YYYY", true);
    if (parsed.isValid()) {
      $hidden.val(parsed.format("YYYY-MM-DD"));
    } else if ($display.val() === "") {
      $hidden.val("");
    }
  }

  $display.on("apply.daterangepicker", function (event, picker) {
    if (minDateValid && picker.startDate.isBefore(minDate, "day")) {
      return;
    }

    $display.val(picker.startDate.format("MM/DD/YYYY"));
    $hidden.val(picker.startDate.format("YYYY-MM-DD"));
  });

  if (!readOnly) {
    $display.on("blur.singleDatePicker change.singleDatePicker", syncFromInput);
  }
}

export default function setupDateRangePickerSearch(options) {
  const {
    formId,
    dateRangeGroups, // This will be an array of objects, e.g., [{ prefix: 'order' }, { prefix: 'shipping' }]
    warningRules = [], // Array of rules for warnings, e.g., [{ younger: 'shipping', older: 'order', message: 'Shipping date cannot be before order date.' }]
    warningContainerSelector = ".warning-section" // Default selector for the warning message container
  } = options;

  // Initialize Date Range Picker for a given set of fields
  function initDateRangePicker(inputSelector, defaultVal, startDateField, endDateField) {
    if (!inputSelector) return;

    const $input = $(inputSelector);
    const $startDate = $(startDateField);
    const $endDate = $(endDateField);

    if ($input.data("daterangepicker")) {
      $input.data("daterangepicker").remove();
      $input.removeData("daterangepicker");
    }

    $input.off("apply.daterangepicker cancel.daterangepicker input blur change");

    const isNarrow = window.matchMedia('(max-width: 563.98px)').matches;
    const pickerOptions = {
      autoUpdateInput: false,
      locale: { format: 'MM/DD/YYYY' },
      // On phones, pin the calendar to the viewport edges so it doesn't sit
      // with a large empty gutter on the left. On desktop, open leftward so
      // the panel doesn't cover the next filter field sitting to the right
      // of this one (these date-range fields are almost always the leftmost
      // field in their row).
      opens: isNarrow ? 'center' : 'left',
      // Without this, daterangepicker auto-flips upward when there isn't enough
      // room below the field, which pushes the calendar up over the fixed navbar.
      drops: 'down',
      ranges: {
        'Today': [moment(), moment()],
        'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
        'Last 7 Days': [moment().subtract(6, 'days'), moment()],
        'Last 30 Days': [moment().subtract(29, 'days'), moment()],
        'This Month': [moment().startOf('month'), moment().endOf('month')],
        'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
      }
    };

    if (defaultVal) {
      const [start, end] = defaultVal.split('-').map(s => s.trim());
      pickerOptions.startDate = start;
      pickerOptions.endDate = end;
      $input.val(`${start} - ${end}`);
      // also populate hidden fields in case defaultVal came from server
      if (moment(start, 'MM/DD/YYYY', true).isValid()) {
        $startDate.val(moment(start, 'MM/DD/YYYY').toISOString());
      }
      if (moment(end, 'MM/DD/YYYY', true).isValid()) {
        $endDate.val(moment(end, 'MM/DD/YYYY').toISOString());
      }
    }

    $input.daterangepicker(pickerOptions);
    watchCalendarForNavbarOverlap($input);

    $input.on('apply.daterangepicker', function (ev, picker) {
      const formattedStart = picker.startDate.format('MM/DD/YYYY');
      const formattedEnd = picker.endDate.format('MM/DD/YYYY');

      $(this).val(`${formattedStart} - ${formattedEnd}`);

      $startDate.val(picker.startDate.toISOString());
      $endDate.val(picker.endDate.toISOString());
    });

    $input.on('cancel.daterangepicker', function () {
      $(this).attr('value', '');
      $startDate.val('');
      $endDate.val('');
    });

    $input.on('input', function () {
      if ($(this).val() === '') {
        $(this).attr('value', '');
        $startDate.val('');
        $endDate.val('');
      }
    });

    // Helper: parse raw typed value like "MM/DD/YYYY - MM/DD/YYYY" and set hidden fields
    function parseAndSetDates(rawVal) {
      if (!rawVal || typeof rawVal !== 'string') return false;
      const parts = rawVal.split('-').map(s => s.trim());
      if (parts.length < 2) return false;

      const start = moment(parts[0], 'MM/DD/YYYY', true);
      const end = moment(parts[1], 'MM/DD/YYYY', true);

      if (start.isValid() && end.isValid()) {
        $startDate.val(start.toISOString());
        $endDate.val(end.toISOString());
        // ensure input value is normalized
        $input.val(`${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`);
        return true;
      }
      return false;
    }

    // If user types dates manually and leaves the field (no 'apply'), parse and populate on blur
    $input.on('blur', function () {
      const raw = $(this).val();
      parseAndSetDates(raw);
    });

    // Also parse on change (covers some paste/auto-fill cases)
    $input.on('change', function () {
      const raw = $(this).val();
      parseAndSetDates(raw);
    });
  }

  // Map to store jQuery objects for start dates, making them accessible by prefix
  const startDateFields = {};

  // Initialize all specified date range pickers
  dateRangeGroups.forEach(group => {
    const inputId = `${group.prefix}_date_range`; // UI field, the User uses to pick a date range.
    const startDateId = `${group.prefix}_date_range_start_date`; // Corresponding date range's start date
    const endDateId = `${group.prefix}_date_range_end_date`;// Corresponding date range's end date

    // Store the jQuery object for the start date field
    startDateFields[group.prefix] = `#${startDateId}`;

    initDateRangePicker(
      `#${inputId}`,          // Selector
      $(`#${inputId}`).val(), // Selector value
      `#${startDateId}`,      // start date field id
      `#${endDateId}`         // end date field id
    );
  });

  // Create a container for the warning message when the host page provides one
  const warningContainer = document.querySelector(warningContainerSelector);
  const notice = document.createElement("div");
  notice.id = "date-warning";
  notice.className = "alert alert-warning mt-2 d-none";
  notice.role = "alert";
  if (warningContainer) {
    warningContainer.prepend(notice);
  }

  // Grab the form and the submit button
  const form = document.querySelector(`#${formId}`);
  const searchButton = form ? form.querySelector("button") : null;

  if (searchButton) {
    // Hook into the search button click event
    searchButton.addEventListener("click", function (e) {
      let showWarning = false;
      let warningMessage = "";

      // Check each warning rule
      for (const rule of warningRules) {
        const youngerStartDateField = $(startDateFields[rule.younger]);
        const olderStartDateField = $(startDateFields[rule.older]);

        const youngerStartDate = moment.utc(youngerStartDateField?.val());
        const olderStartDate = moment.utc(olderStartDateField?.val());

        if (youngerStartDate.isValid() && olderStartDate.isValid() && youngerStartDate.isBefore(olderStartDate)) {
          showWarning = true;
          warningMessage = rule.message;
          break; // Stop on the first rule violation
        }
      }

      if (showWarning) {
        e.preventDefault(); // Prevent form submission
        notice.innerText = warningMessage; // Set the specific warning message
        notice.classList.remove("d-none"); // Show the warning
        $('html, body').animate({ scrollTop: $('#date-warning').offset().top - 100 }, 300); // Scroll to the warning
        return false;
      } else {
        // Hide the warning if no rules are violated
        notice.classList.add("d-none");
      }
    });
  }
}
